import type { InjectionKey } from 'vue'
import type { DemandeStore } from '@/features/interface/demande/shared'
import { loadExtraDataDossierDRF } from '@/features/services/demande'
import type { NavigateToDemandeSor } from '@/router/navigateTo'
import { demandeToLocation } from '@/features/application/locations'
import type { CourrierDTO } from '@/features/api/extranet/dto/Dossier/Synthese/CourrierDTO'
import { useAppSnackBar } from '@/features/interface/shared/snack-bar'

type DocumentsRefreshCXPI = ReturnType<typeof useDocumentsRefreshCXPI>
export const CXPI_DOC_REFRESH: InjectionKey<DocumentsRefreshCXPI> = Symbol('cxpi-actions')

export function useDocumentsRefreshCXPI(store: DemandeStore<'MODEL_CXPI'>) {
  let disposed = false
  let timeOut: ReturnType<typeof setTimeout> | undefined = undefined
  const REFRESH_INTERVALS = [2_000, 5_000, 10_000, 20_000, 60_000, 300_000] as const
  let refreshIntervals: number[] = []

  const snackBar = useAppSnackBar()


  const location = demandeToLocation({
    typeDemande: 'ReservationFonds',
    origine: 'sor',
    dispositif: store.model!.dispositif,
    sousDispositif: store.model!.sousDispositif,
    code: store.model!.contrat!.codeDrf,
  }) as NavigateToDemandeSor

  function updateCourriers(courriers: CourrierDTO[]) {
    if (disposed) return
    const doUpdate = store.setupCustomUpdater<CourrierDTO[]>((patch, courriers) => {
      patch.drf!.courriers = courriers
    })
    return store.preserveEstModifié(() => doUpdate(courriers))
  }

  function scheduleRefresh() {
    if (timeOut) clearTimeout(timeOut)
    if (disposed) return

    const interval = refreshIntervals.shift()
    // console.log('scheduling refresh in', interval ?? 'undefined')
    if (interval) {
      timeOut = setTimeout(doRefresh, interval)
    } else {
      timeOut = undefined
    }
  }

  async function doRefresh(forced: boolean = false) {
    // console.log('refreshing drf')
    const drf = await loadExtraDataDossierDRF(location, { dialogMode: 'silent', sablier: forced })
    if (disposed) return

    const existModifs = (drf.data.courriers ?? []).length !== (store.model?.drf?.courriers ?? []).length
    if (existModifs) {
      try {
        await updateCourriers(drf.data?.courriers ?? [])
        snackBar.pop({
          type: 'success',
          message: 'Le ou les courriers DRF ont été réceptionnés avec succès',
        })
        refreshIntervals = []
      } catch (error) {
        console.error(error)
      }
    } else {
      if (!forced)
        scheduleRefresh()
    }
  }

  async function forceRaffraichissement() {
    return doRefresh(true)
  }

  function notifyEnvoieCourrier() {
    refreshIntervals = [...REFRESH_INTERVALS]
    scheduleRefresh()
  }

  return {
    forceRaffraichissement,
    notifyEnvoieCourrier,
    dispose: () => {
      disposed = true
      if (timeOut) clearTimeout(timeOut)
      // console.log('useDocumentsRefreshCXPI disposed')
    },
  }
}
